import type { TimePoint } from '@vedalib/editor/lib/elements'
import React from 'react'

import { TooltipCourse } from 'components/Editor/EditorElements/courseKit/DropDownCourse'
import { videoFormat } from 'utils/editorUtils'

import type { MediaStyles } from '../../types'
import { MediaControlType } from '../types'
import { useMediaT } from '../useMediaT'
import {
  DisabledOverlay,
  PointStyled,
  PointsWrapperStyled,
  SliderLoaded,
  SliderPlayed,
  SliderStyled,
  SliderTrack,
  SliderWrapper,
} from './styled'

const REWIND_STEP = 5 // seconds

interface ISeekSliderProps {
  onStartSeeking: () => void
  onSetPlayed: (seconds: number) => void
  onSeekTo: (seconds: number) => void
  played?: number
  loaded?: number
  duration: number
  styles?: Partial<Record<MediaStyles, React.CSSProperties>>
  points: TimePoint[]
  preview?: boolean
  currentPoint?: TimePoint | null
  disableSeeking?: boolean
  maxPlayed?: number
  isCourse?: boolean
}

const SeekSlider: React.FC<ISeekSliderProps> = ({
  played = 0,
  loaded = 0,
  duration,
  styles,
  points,
  onSetPlayed,
  onStartSeeking,
  onSeekTo,
  preview,
  currentPoint,
  disableSeeking,
  maxPlayed = 0,
  isCourse = false,
}) => {
  const tt = useMediaT(isCourse)
  const clamp = (value: number) => (disableSeeking ? Math.min(value, maxPlayed) : value)

  const handleStartSeeking = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent> | React.TouchEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation()
    onStartSeeking()
  }

  const handleSeekTo = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent> | React.TouchEvent<HTMLInputElement>,
  ) => {
    event.stopPropagation()
    const value = clamp(parseFloat(event.currentTarget.value))
    onSeekTo(value * duration)
  }

  const handleSetPlayed = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    const value = clamp(parseFloat(event.currentTarget.value))
    onSetPlayed(value * duration)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      const absStep = REWIND_STEP / duration
      const step = event.key === 'ArrowLeft' ? -absStep : absStep
      let newValue = Math.min(Math.max(played + step, 0), 1)
      newValue = clamp(newValue)
      onSeekTo(newValue * duration)
    }
  }

  const slider = (
    <SliderWrapper>
      <SliderTrack $preview={preview} $styles={styles}>
        <SliderLoaded $loaded={loaded} $styles={styles} />
        <SliderPlayed $played={played} $styles={styles} />
        <PointsWrapperStyled>
          {points.map(({ time }) => (
            <PointStyled $position={(time / duration) * 100} $styles={styles} key={time} />
          ))}
        </PointsWrapperStyled>
      </SliderTrack>
      <SliderStyled
        aria-label='Seek slider'
        aria-valuemax={duration}
        aria-valuemin={0}
        aria-valuenow={played * duration}
        aria-valuetext={`${currentPoint?.title ? `${currentPoint.title}, ` : ''}${videoFormat(Math.floor(played * duration))} of ${videoFormat(duration)}`}
        data-player-control={MediaControlType.Seek}
        max={1}
        min={0}
        onChange={handleSetPlayed}
        onKeyDown={handleKeyDown}
        onMouseDown={handleStartSeeking}
        onMouseUp={handleSeekTo}
        onTouchEnd={handleSeekTo}
        onTouchEndCapture={handleSeekTo}
        onTouchStart={handleStartSeeking}
        onTouchStartCapture={handleStartSeeking}
        step={0.001}
        type='range'
        value={played}
      />
      {disableSeeking && (
        <TooltipCourse overlay={tt('elements.media.seekingDisabled')} placement='top'>
          <DisabledOverlay $left={maxPlayed} />
        </TooltipCourse>
      )}
    </SliderWrapper>
  )

  return slider
}

export default SeekSlider
